import { AdFormat, LAZY_LOAD_SETTINGS, AD_SIZE_MAPPINGS, OutOfPageTypes, REFRESH_TIME_AFTER_VIEWABILITY } from './constants';
import React, { createContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { debounce, getCookie } from './utils';
import { useRedline } from '@libs/redline';
import SHA256 from 'crypto-js/sha256';

// eslint-disable-next-line @nx/enforce-module-boundaries
import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';

// Types
interface SlotInfo {
  id: string;
  path: string;
  slot: googletag.Slot | null;
  isEmpty?: boolean;
}

interface SlotOptions {
  className?: string;
  format?: AdFormat;
  sizeMapping?: googletag.SizeMappingArray;
  targetings?: { key: string; value: string }[];
}

interface AdsContextType {
  slots: Record<string, SlotInfo>;
  createSlot: (id: string, path: string, options: SlotOptions) => googletag.Slot | undefined;
  createOutOfPageSlot: (
    path: string,
    div: string | OutOfPageTypes,
    targetings: { key: string; value: string }[] | undefined
  ) => googletag.Slot | undefined;
  createInterstitialSlot: (path: string, targetings: { key: string; value: string }[] | undefined) => googletag.Slot | undefined;
  destroySlot: (slot: googletag.Slot) => void;
  refreshSlot: (id: string) => void;
  refreshAllSlots: () => void;
  getSlotInfo: (id: string) => SlotInfo | undefined;
  disableAds: boolean;
}

// Create context
const AdsContext = createContext<AdsContextType | undefined>(undefined);

// Provider component
export const AdsProvider: React.FC<{ children: ReactNode; disableAds?: boolean }> = ({ children, disableAds = false }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [slots, setSlots] = useState<Record<string, SlotInfo>>({});

  const timeoutIdRef = useRef<Record<string, NodeJS.Timeout>>({});

  const { track } = useRedline();

  const { authenticatedUser } = useAuthenticatedUser();
  const document = authenticatedUser?.customer?.document;

  // Initialize Google Ad Manager
  useEffect(() => {
    if (disableAds) return;
    if (!window.googletag?.cmd) console.warn('Google Ad Manager not loaded.');

    window.googletag = window.googletag || {};
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    googletag.cmd = googletag.cmd || [];

    const intesParams = new URLSearchParams(window.location.search);
    const isInterstitialPage = intesParams.get('ad_int') === 'true';

    const { hostname } = window.location;
    const startURL = getCookie('start_source_url');

    let urlParams: Record<string, string> = {};
    if (startURL) {
      try {
        const url = new URL(startURL);
        urlParams = Object.fromEntries(url.searchParams.entries());
      } catch (error) {
        console.warn('Invalid URL in start_source_url cookie:', error);
      }
    }

    const mediumTargeting = ['pmax', 'display', 'cpc'].includes(urlParams['utm_medium']) ? urlParams['utm_medium'] : 'others';

    const isConsumidorPositivo = hostname.includes('consumidorpositivo.com.br');
    const mediumTargetingKey = isConsumidorPositivo ? 'medium_cp' : 'medium';

    window.googletag.cmd.push(() => {
      const pubads = googletag.pubads();
      if (document) {
        const hashedPpid = SHA256(document).toString();
        pubads.setPublisherProvidedId(hashedPpid);
      }

      if (isInterstitialPage) {
        pubads.enableSingleRequest();
      } else {
        pubads.disableInitialLoad();
        pubads.set('page_url', window.location.origin);
        pubads.setCentering(true);
        pubads.collapseEmptyDivs();

        // Set medium targeting
        pubads.setTargeting(mediumTargetingKey, mediumTargeting);

        pubads.enableLazyLoad(LAZY_LOAD_SETTINGS);
      }

      // Set configs targeting
      googletag.enableServices();

      setIsInitialized(true);
    });

    return () => {
      window.googletag.cmd.push(() => {
        // Destroy all slots
        googletag.destroySlots();

        // Clear all timeouts
        Object.values(timeoutIdRef.current).forEach(clearTimeout);
        timeoutIdRef.current = {};
      });
    };
  }, []);

  // Define event handlers with useCallback
  const handleSlotRequested = useCallback((event: googletag.events.SlotRequestedEvent) => {
    // TODO: Handle slot request tracking
  }, []);

  const handleSlotResponseReceived = useCallback((event: googletag.events.SlotResponseReceived) => {
    // TODO: Handle slot response received tracking
  }, []);

  const handleSlotOnload = useCallback(
    (event: googletag.events.SlotOnloadEvent) => {
      const { slot } = event;

      // Tracking code
      const payload = {
        location: window.location.pathname,
        adUnitPath: slot.getAdUnitPath(),
        slotElementId: slot.getSlotElementId(),
      };

      track.userTracking.adLoaded(payload);
    },
    [track.userTracking]
  );

  const handleImpressionViewable = useCallback(
    (event: googletag.events.ImpressionViewableEvent) => {
      const { slot } = event;
      const id = slot.getSlotElementId();

      // Tracking code
      const payload = {
        location: window.location.pathname,
        adUnitPath: slot.getAdUnitPath(),
        slotElementId: slot.getSlotElementId(),
      };

      track.userTracking.adViewed(payload);

      // Set up automatic refresh
      if (timeoutIdRef.current[id]) {
        clearTimeout(timeoutIdRef.current[id]);
      }

      timeoutIdRef.current[id] = setTimeout(() => {
        googletag.pubads().refresh([slot]);
      }, 1000 * REFRESH_TIME_AFTER_VIEWABILITY);
    },
    [track.userTracking]
  );

  const handleSlotRenderEnded = useCallback((event: googletag.events.SlotRenderEndedEvent) => {
    // TODO: Handle slot render ended tracking
    const { slot, isEmpty } = event;
    const id = slot.getSlotElementId();

    setSlots((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        isEmpty: !prev[id]?.isEmpty ? false : isEmpty,
      },
    }));
  }, []);

  // Helper function to manage event listeners with proper typing
  const useGoogleTagEventListener = <K extends keyof googletag.events.EventTypeMap>(
    eventName: K,
    handler: (event: googletag.events.EventTypeMap[K]) => void,
    enabled = true
  ) => {
    useEffect(() => {
      if (!enabled) return;

      googletag.pubads().addEventListener(eventName, handler);

      return () => {
        googletag.pubads().removeEventListener(eventName, handler);
      };
    }, [eventName, handler, enabled]);
  };

  // Set up individual event listeners with proper event types
  useGoogleTagEventListener('slotRequested', handleSlotRequested, isInitialized);
  useGoogleTagEventListener('slotResponseReceived', handleSlotResponseReceived, isInitialized);
  useGoogleTagEventListener('slotOnload', handleSlotOnload, isInitialized);
  useGoogleTagEventListener('impressionViewable', handleImpressionViewable, isInitialized);
  useGoogleTagEventListener('slotRenderEnded', handleSlotRenderEnded, isInitialized);

  // Debounce function to trigger refresh
  const debounceRefresh = debounce(() => {
    window.googletag.cmd.push(() => {
      window.googletag.pubads().refresh();
    });
  }, 100);

  // Create a slot
  const createSlot = useCallback(
    (id: string, path: string, options: SlotOptions) => {
      if (!isInitialized) {
        console.warn('Google Ad Manager not initialized');
        return;
      }

      // Define slot
      const slot = googletag.defineSlot(path, [0, 0], id);
      if (!slot) {
        console.warn(`Failed to define slot for ID: ${id}`);
        return;
      }

      // Configure size mapping
      const effectiveMapping =
        options.sizeMapping || (options.format ? AD_SIZE_MAPPINGS[options.format as AdFormat] : AD_SIZE_MAPPINGS['responsive']);

      const mapping = googletag.sizeMapping();
      effectiveMapping.forEach(([viewport, sizes]) => {
        mapping.addSize(viewport, sizes);
      });
      const builtMapping = mapping.build();

      if (builtMapping) slot.defineSizeMapping(builtMapping);

      // Set targetings
      if (options.targetings?.length) {
        for (const { key, value } of options.targetings) {
          slot.setTargeting(key, value);
        }
      }

      slot.addService(googletag.pubads());

      // Update state
      setSlots((prev) => ({
        ...prev,
        [id]: {
          id,
          path,
          slot,
        },
      }));

      // Display ad
      debounceRefresh();

      return slot;
    },
    [isInitialized]
  );

  // Create an out of page slot
  const createOutOfPageSlot = useCallback(
    (path: string, div: string | OutOfPageTypes, targetings?: { key: string; value: string }[]) => {
      if (!isInitialized) {
        console.warn('Google Ad Manager not initialized');
        return;
      }

      const slot = googletag.defineOutOfPageSlot(path, div as string | googletag.enums.OutOfPageFormat);
      if (!slot) return;

      slot.addService(googletag.pubads());

      // Set targetings
      if (targetings?.length) {
        for (const { key, value } of targetings) {
          slot.setTargeting(key, value);
        }
      }

      const id = slot.getSlotElementId();

      // Update state
      setSlots((prev) => ({
        ...prev,
        [id]: {
          id,
          path,
          slot,
        },
      }));

      // Display ad
      window.googletag.pubads().refresh([slot]);

      return slot;
    },
    [isInitialized]
  );

  const createInterstitialSlot = useCallback(
    (path: string, targetings?: { key: string; value: string }[]) => {
      if (!isInitialized) {
        console.warn('Google Ad Manager not initialized');
        return;
      }

      const slot = googletag.defineOutOfPageSlot(path, googletag.enums?.OutOfPageFormat.INTERSTITIAL);
      if (!slot) {
        return;
      }

      slot.addService(googletag.pubads());

      // Set targetings
      if (targetings?.length) {
        for (const { key, value } of targetings) {
          slot.setTargeting(key, value);
        }
      }

      const id = slot.getSlotElementId();

      // Update state
      setSlots((prev) => ({
        ...prev,
        [id]: {
          id,
          path,
          slot,
        },
      }));

      return slot;
    },
    [isInitialized]
  );

  // Destroy a slot
  const destroySlot = useCallback((slot: googletag.Slot) => {
    const id = slot.getSlotElementId();

    // Clear refresh timeout if it exists
    if (timeoutIdRef.current[id]) {
      clearTimeout(timeoutIdRef.current[id]);
      delete timeoutIdRef.current[id];
    }

    window.googletag.cmd.push(() => {
      if (slot) {
        googletag.destroySlots([slot]);
      }

      // Update state
      setSlots((prev) => {
        const newSlots = { ...prev };
        delete newSlots[id];
        return newSlots;
      });
    });
  }, []);

  // Refresh a specific slot
  const refreshSlot = useCallback(
    (id: string) => {
      const slotInfo = { ...slots[id] };
      if (!slotInfo || !slotInfo.slot) return;

      // Clear refresh timeout if it exists
      if (timeoutIdRef.current[id]) {
        clearTimeout(timeoutIdRef.current[id]);
        delete timeoutIdRef.current[id];
      }

      window.googletag.cmd.push(() => {
        if (slotInfo.slot) {
          googletag.pubads().refresh([slotInfo.slot]);
        }
      });
    },
    [slots]
  );

  // Refresh all slots
  const refreshAllSlots = useCallback(() => {
    const activeSlots = Object.values(slots)
      .map((slotInfo) => slotInfo.slot)
      .filter(Boolean) as googletag.Slot[];

    if (activeSlots.length === 0) return;

    // Clear all refresh timeouts
    Object.keys(timeoutIdRef.current).forEach((id) => {
      clearTimeout(timeoutIdRef.current[id]);
      delete timeoutIdRef.current[id];
    });

    window.googletag.cmd.push(() => {
      googletag.pubads().refresh(activeSlots);
    });
  }, [slots]);

  // Get information about a specific slot
  const getSlotInfo = useCallback(
    (id: string) => {
      const slot = { ...slots[id] };
      return slot;
    },
    [slots]
  );

  // Context value
  const contextValue = {
    slots,
    createOutOfPageSlot,
    createInterstitialSlot,
    createSlot,
    destroySlot,
    refreshSlot,
    refreshAllSlots,
    getSlotInfo,
    disableAds,
  };

  return <AdsContext.Provider value={contextValue}>{children}</AdsContext.Provider>;
};

export { AdsContext };
